// Step 5d Phase 4 — offline auth: device enrolment, PIN profiles, unlock/lockout, the
// uniform 7-day window, and device purge.
//
// CARDINAL RULE: none of this substitutes for server auth. A profile unlock only unwraps
// the local DEK so the device can read its OWN encrypted cache; every server request still
// presents real tokens and is authorised by the server. The PIN gates bytes, not authority.

import { getOfflineDb, closeOfflineDb, OFFLINE_DB_NAME, type DeviceRecord, type ProfileRecord } from './db';
import { deriveKekRaw, generateDek, wrapDek, unwrapDek, randomBytes, DEFAULT_KDF_PARAMS } from './crypto';
import { getDek, setUnlockedDek, clearDek } from './dekSession';
import { getDeviceId, resetDeviceId, reencryptLegacyOps } from './opQueue';

const DEVICE_KEY = 'this_device';
const PIN_MIN_LEN = 6;
export const OFFLINE_WINDOW_MS = 7 * 24 * 60 * 60 * 1000; // uniform 7-day offline session

// Escalating failed-unlock delays (ms) by attempt number. 1–4 free; terminal lock at 10.
// Lock NEVER wipes — queued ops survive (the real security is Argon2id + 7-day + revocation).
const DELAY_BY_ATTEMPT: Record<number, number> = {
  5: 30_000, 6: 60_000, 7: 5 * 60_000, 8: 15 * 60_000, 9: 60 * 60_000,
};
export const TERMINAL_ATTEMPTS = 10;

// ── Device record ───────────────────────────────────────────────────────────
export async function getDeviceRecord(): Promise<DeviceRecord | null> {
  try { const db = await getOfflineDb(); return (await db.get('device', DEVICE_KEY)) ?? null; }
  catch { return null; }
}
async function putDeviceRecord(rec: DeviceRecord): Promise<void> {
  const db = await getOfflineDb();
  await db.put('device', rec, DEVICE_KEY);
}

// ── Profiles ──────────────────────────────────────────────────────────────────
export async function listProfiles(): Promise<ProfileRecord[]> {
  try { const db = await getOfflineDb(); return await db.getAll('profiles'); }
  catch { return []; }
}
export async function getProfile(userId: string): Promise<ProfileRecord | null> {
  try { const db = await getOfflineDb(); return (await db.get('profiles', userId)) ?? null; }
  catch { return null; }
}
async function putProfile(p: ProfileRecord): Promise<void> {
  const db = await getOfflineDb();
  await db.put('profiles', p);
}

export interface AuthUserLite { id: string; preferred_name?: string | null; full_name?: string; email?: string }

// ── Enrolment (ONLINE) ─────────────────────────────────────────────────────────
// First enrolment on a device generates the device DEK (and unlocks the session).
// Subsequent users wrap the SAME DEK with their own KEK — which REQUIRES the DEK to be in
// memory (an existing profile must be unlocked, or this is the first enrolment). This is
// the secure shared-tablet provisioning model: no DEK without a PIN.
export async function enrollProfile(user: AuthUserLite, pin: string): Promise<{ ok: true } | { ok: false; error: string }> {
  if (!/^\d{6,}$/.test(pin) || pin.length < PIN_MIN_LEN) {
    return { ok: false, error: `PIN must be at least ${PIN_MIN_LEN} digits.` };
  }
  try {
    const profiles = await listProfiles();
    let dekRaw: Uint8Array | null = getDek();
    if (profiles.length === 0) {
      dekRaw = generateDek();           // first enrolment establishes the device DEK
      setUnlockedDek(dekRaw, user.id);
    } else if (!dekRaw) {
      return { ok: false, error: 'Unlock an existing profile on this device first to add a user.' };
    }
    const salt = randomBytes(16);
    const kek = await deriveKekRaw(pin, salt);
    const wrapped = await wrapDek(dekRaw!, kek);
    const now = new Date().toISOString();
    await putProfile({
      user_id: user.id,
      display_name: user.preferred_name || user.full_name || user.email || user.id,
      salt, kdf: { ...DEFAULT_KDF_PARAMS }, wrapped_dek: wrapped,
      failed_attempts: 0, locked_until: null, terminal_locked: false, created_at: now,
    });
    // Establish / refresh the device record + online-auth stamp; request durable storage.
    const existing = await getDeviceRecord();
    let granted: boolean | null = existing?.persistent_storage ?? null;
    try { if (navigator.storage?.persist && !granted) granted = await navigator.storage.persist(); } catch { /* ignore */ }
    await putDeviceRecord({
      device_id: getDeviceId(),
      enrolled_at: existing?.enrolled_at ?? now,
      persistent_storage: granted,
      last_online_auth_at: now, // enrolment is online
    });
    // Migration cleanup: encrypt any legacy plaintext op_queue rows now the DEK exists.
    await reencryptLegacyOps();
    return { ok: true };
  } catch (e) {
    console.warn('[offline] enrollProfile failed', e);
    return { ok: false, error: 'Could not set up this device. Try again.' };
  }
}

// ── PIN change / reset (PR-000042 light interim — Profile page, this device only) ──
// Both re-wrap the DEK under a new KEK: the PIN changes, the DEK (and therefore the whole
// cached dataset) is untouched. No server involvement — the PIN stays local per device.
// Neither applies the gate's escalating lockout: these forms sit BEHIND server auth (the
// Profile page), so the unlock gate's brute-force machinery is not the control here.

/** Change the PIN by proving the current one (wrong current PIN → friendly error). */
export async function changePin(userId: string, currentPin: string, newPin: string): Promise<{ ok: true } | { ok: false; error: string }> {
  if (!/^\d{6,}$/.test(newPin)) {
    return { ok: false, error: `PIN must be at least ${PIN_MIN_LEN} digits.` };
  }
  try {
    const p = await getProfile(userId);
    if (!p) return { ok: false, error: 'No PIN is set up on this device yet.' };
    let dekRaw: Uint8Array;
    try {
      const currentKek = await deriveKekRaw(currentPin, p.salt, p.kdf as typeof DEFAULT_KDF_PARAMS);
      dekRaw = await unwrapDek(p.wrapped_dek, currentKek); // THROWS on wrong PIN (GCM auth)
    } catch {
      return { ok: false, error: 'Your current PIN is incorrect.' };
    }
    const salt = randomBytes(16);
    const kek = await deriveKekRaw(newPin, salt);
    const wrapped = await wrapDek(dekRaw, kek);
    await putProfile({
      ...p, salt, kdf: { ...DEFAULT_KDF_PARAMS }, wrapped_dek: wrapped,
      failed_attempts: 0, locked_until: null, terminal_locked: false,
    });
    return { ok: true };
  } catch (e) {
    console.warn('[offline] changePin failed', e);
    return { ok: false, error: 'Could not change the PIN. Try again.' };
  }
}

/**
 * Reset the PIN WITHOUT the current one — the caller must have verified the account
 * password server-side first (step-up), and the DEK must be unlocked in memory (it is on
 * the Profile page: the OfflineAuthGate stands in front of the routed app).
 */
export async function resetPin(userId: string, newPin: string): Promise<{ ok: true } | { ok: false; error: string }> {
  if (!/^\d{6,}$/.test(newPin)) {
    return { ok: false, error: `PIN must be at least ${PIN_MIN_LEN} digits.` };
  }
  try {
    const dekRaw = getDek();
    if (!dekRaw) return { ok: false, error: 'Please reload and unlock, then try again.' };
    const p = await getProfile(userId);
    if (!p) return { ok: false, error: 'No PIN is set up on this device yet.' };
    const salt = randomBytes(16);
    const kek = await deriveKekRaw(newPin, salt);
    const wrapped = await wrapDek(dekRaw, kek);
    await putProfile({
      ...p, salt, kdf: { ...DEFAULT_KDF_PARAMS }, wrapped_dek: wrapped,
      failed_attempts: 0, locked_until: null, terminal_locked: false,
    });
    return { ok: true };
  } catch (e) {
    console.warn('[offline] resetPin failed', e);
    return { ok: false, error: 'Could not reset the PIN. Try again.' };
  }
}

// ── Unlock + lockout state machine ──────────────────────────────────────────────
export interface UnlockState {
  enrolled: boolean;
  terminalLocked: boolean;     // 10 failures → online re-auth required
  lockedUntilMs: number | null; // transient escalating-delay lock (epoch ms), if in future
  failedAttempts: number;
}

export async function unlockStateFor(userId: string): Promise<UnlockState> {
  const p = await getProfile(userId);
  if (!p) return { enrolled: false, terminalLocked: false, lockedUntilMs: null, failedAttempts: 0 };
  const lu = p.locked_until && p.locked_until > Date.now() ? p.locked_until : null;
  return { enrolled: true, terminalLocked: p.terminal_locked, lockedUntilMs: lu, failedAttempts: p.failed_attempts };
}

export async function unlockProfile(userId: string, pin: string): Promise<{ ok: true } | { ok: false; error: string; state: UnlockState }> {
  const p = await getProfile(userId);
  if (!p) return { ok: false, error: 'No profile on this device.', state: { enrolled: false, terminalLocked: false, lockedUntilMs: null, failedAttempts: 0 } };
  if (p.terminal_locked) {
    return { ok: false, error: 'Too many attempts — reconnect and sign in online to restore this device.', state: await unlockStateFor(userId) };
  }
  if (p.locked_until && p.locked_until > Date.now()) {
    return { ok: false, error: 'Too many attempts — wait before trying again.', state: await unlockStateFor(userId) };
  }
  try {
    const kek = await deriveKekRaw(pin, p.salt, p.kdf as typeof DEFAULT_KDF_PARAMS);
    const dekRaw = await unwrapDek(p.wrapped_dek, kek); // THROWS on wrong PIN (GCM auth) — no plaintext leaks
    // Success — reset the counter, establish the DEK.
    await putProfile({ ...p, failed_attempts: 0, locked_until: null });
    setUnlockedDek(dekRaw, userId);
    return { ok: true };
  } catch {
    // Wrong PIN (or tampered wrap). Escalate.
    const attempts = p.failed_attempts + 1;
    const terminal = attempts >= TERMINAL_ATTEMPTS;
    const delay = DELAY_BY_ATTEMPT[attempts] ?? 0;
    await putProfile({
      ...p,
      failed_attempts: attempts,
      terminal_locked: terminal,
      locked_until: delay > 0 ? Date.now() + delay : null,
    });
    const state = await unlockStateFor(userId);
    const msg = terminal
      ? 'Too many attempts — reconnect and sign in online to restore this device.'
      : delay > 0
        ? `Incorrect PIN. Locked for ${Math.round(delay / 1000)}s.`
        : 'Incorrect PIN.';
    return { ok: false, error: msg, state };
  }
}

// ── 7-day offline window + online-auth stamp ─────────────────────────────────────
// Called on every successful ONLINE auth (login / silent refresh). Re-stamps the window
// and clears any terminal lock on the current user's profile (online re-auth restores).
export async function recordOnlineAuth(userId?: string): Promise<void> {
  try {
    const rec = await getDeviceRecord();
    if (rec) await putDeviceRecord({ ...rec, last_online_auth_at: new Date().toISOString() });
    if (userId) {
      const p = await getProfile(userId);
      if (p && (p.terminal_locked || p.failed_attempts > 0 || p.locked_until)) {
        await putProfile({ ...p, terminal_locked: false, failed_attempts: 0, locked_until: null });
      }
    }
  } catch { /* best-effort */ }
}

/** True when the uniform 7-day offline window has elapsed since the last online auth. */
export async function isOfflineExpired(): Promise<boolean> {
  const rec = await getDeviceRecord();
  if (!rec) return false; // not enrolled — no offline session to expire
  return Date.now() - new Date(rec.last_online_auth_at).getTime() > OFFLINE_WINDOW_MS;
}

// ── Purge (remote revocation honouring / unenrol) ────────────────────────────────
// Deletes the ENTIRE offline DB (cache + profiles + queues + device record), clears the
// in-memory DEK, and resets the device id so the next enrolment is a fresh device.
export async function purgeDevice(): Promise<void> {
  clearDek();
  try {
    await closeOfflineDb(); // must close the connection before delete
    const { deleteDB } = await import('idb');
    await deleteDB(OFFLINE_DB_NAME);
  } catch (e) {
    console.warn('[offline] purgeDevice: deleteDB failed', e);
  }
  resetDeviceId();
}
